import { ApprovalFlowStatus } from '@/constants/ApprovalConstants';
import { formatDateFromSlashToDash } from '@/utils/formatDate';
import { extraTripTodayKey } from '@/utils/isEmergencyExtraTripFlow';

const terminalDayStatuses = [
	'Cancelado',
	'Cancelado por falta de ação',
	'Recusado',
];

export function canCancelExtraTripDay({
	approvalStatus,
	dayDate,
	dayStatus,
	now = new Date(),
}: {
	approvalStatus: string;
	dayDate: string;
	dayStatus: string;
	now?: Date;
}): boolean {
	if (approvalStatus !== ApprovalFlowStatus.ACCEPTED) return false;
	if (terminalDayStatuses.includes(dayStatus)) return false;
	if (!dayDate || dayDate === '-') return false;
	return formatDateFromSlashToDash(dayDate) > extraTripTodayKey(now);
}

export const getMotiveText = (motive: string) => {
	switch (motive) {
		case 'Absenteeism':
			return 'Absenteísmo';
		case 'VacancyCovering':
			return 'Cobertura de Vaga';
		case 'SpecialServices':
			return 'Serviços especiais';
		default:
			return '-';
	}
};

export const getStatusText = (status: ApprovalFlowStatus) => {
	switch (status) {
		case ApprovalFlowStatus.ACCEPTED:
			return 'Aprovado';
		case ApprovalFlowStatus.REJECTED:
			return 'Reprovado';
		case ApprovalFlowStatus.LACK_OF_ACTION:
			return 'Cancelado por falta de ação';
		default:
			return '-';
	}
};

/**
 * Get the label, background color, and text color of the approval status. It is used to display the badge of the days in `ExtraTripCard`.
 * @param status The status of the approval
 * @returns The label, background color, and text color of the approval status
 */
export function getDayStatusLabel(status: string): {
	label: string;
	bgColor: string;
	textColor: string;
} {
	switch (status) {
		case 'Aceito':
			return {
				label: 'Aprovado',
				bgColor: 'bg-[#D0FFDA]',
				textColor: 'text-[#12A031]',
			};
		case 'Pendente':
			return {
				label: 'Em aprovação',
				bgColor: 'bg-[#FBFFC2]',
				textColor: 'text-[#977107]',
			};
		case 'Recusado':
			return {
				label: 'Recusado',
				bgColor: 'bg-[#FFDBDF]',
				textColor: 'text-[#CE0C23]',
			};
		case 'Cancelado':
			return {
				label: 'Cancelado',
				bgColor: 'bg-[#FFDBDF]',
				textColor: 'text-[#CE0C23]',
			};
		case 'Cancelado por falta de ação':
			return {
				label: 'Cancelado por falta de ação',
				bgColor: 'bg-[#FFDBC2]',
				textColor: 'text-[#925327]',
			};
		default:
			return {
				label: status || '-',
				bgColor: 'bg-gray-100',
				textColor: 'text-gray-700',
			};
	}
}

export function getApprovalStatusLabel(status: string): {
	label: string;
	bgColor: string;
	textColor: string;
} {
	switch (status) {
		case ApprovalFlowStatus.ACCEPTED:
			return {
				label: 'Aprovada',
				bgColor: 'bg-[#D0FFDA]',
				textColor: 'text-[#12A031]',
			};
		case ApprovalFlowStatus.PENDING:
			return {
				label: 'Em aprovação',
				bgColor: 'bg-[#FBFFC2]',
				textColor: 'text-[#977107]',
			};
		case ApprovalFlowStatus.REJECTED:
			return {
				label: 'Recusada',
				bgColor: 'bg-[#FFDBDF]',
				textColor: 'text-[#CE0C23]',
			};
		case ApprovalFlowStatus.CANCELLED:
			return {
				label: 'Cancelada',
				bgColor: 'bg-[#FFDBDF]',
				textColor: 'text-[#CE0C23]',
			};
		case ApprovalFlowStatus.LACK_OF_ACTION:
			return {
				label: 'Cancelada por falta de ação',
				bgColor: 'bg-[#FFDBC2]',
				textColor: 'text-[#925327]',
			};
		default:
			return {
				label: status || '-',
				bgColor: 'bg-gray-100',
				textColor: 'text-gray-700',
			};
	}
}
